import { useState, useEffect, useRef } from 'react';
import { useTestStore, type TestId } from '@/store/useTestStore';
import { t } from '@/i18n/translations';
import { TestCard } from '@/components/TestCard';
import { MetricBox } from '@/components/MetricBox';

interface Props { autoRun: boolean; autoNonce: number; }

const INPUT_TYPES = ['text', 'email', 'tel', 'url', 'number', 'date', 'time', 'color', 'range', 'search'] as const;

export function FormKeyboardTest({ autoRun, autoNonce }: Props) {
  const { language, reportResult, onAutoDone } = useTestStore();
  const [running, setRunning] = useState(false);
  const [metrics, setMetrics] = useState({
    visualViewport: !!window.visualViewport,
    keyboardDetected: false,
    supportedTypes: 0,
    totalTypes: INPUT_TYPES.length,
    autofillSupported: false,
    viewportResizes: 0,
  });
  const autoDoneRef = useRef(false);

  function checkInputTypeSupport(): number {
    let count = 0;
    for (const type of INPUT_TYPES) {
      const input = document.createElement('input');
      input.type = type;
      if (input.type === type) count++;
    }
    return count;
  }

  function reset() {
    setRunning(false);
    autoDoneRef.current = false;
    setMetrics({
      visualViewport: !!window.visualViewport, keyboardDetected: false,
      supportedTypes: 0, totalTypes: INPUT_TYPES.length,
      autofillSupported: false, viewportResizes: 0,
    });
  }

  function start() {
    reset();
    setRunning(true);
    const supported = checkInputTypeSupport();
    setMetrics((m) => ({
      ...m,
      supportedTypes: supported,
      autofillSupported: 'autocomplete' in document.createElement('input'),
    }));
  }

  function stop() {
    setRunning(false);
    let sc = 30;
    if (metrics.visualViewport) sc += 20;
    sc += Math.round((metrics.supportedTypes / metrics.totalTypes) * 30);
    if (metrics.autofillSupported) sc += 10;
    if (metrics.keyboardDetected) sc += 10;
    sc = Math.min(100, sc);

    const testId: TestId = 'mobile-form';
    reportResult(testId, {
      score: sc,
      primary: `${metrics.supportedTypes}/${metrics.totalTypes} types · VV: ${metrics.visualViewport ? 'YES' : 'NO'}`,
      details: metrics,
    });
    if (autoRun && !autoDoneRef.current) { autoDoneRef.current = true; onAutoDone(testId); }
  }

  // Listen for visual viewport resize (keyboard appearance)
  useEffect(() => {
    if (!running || !window.visualViewport) return;
    function onResize() {
      setMetrics((m) => ({
        ...m,
        viewportResizes: m.viewportResizes + 1,
        keyboardDetected: true,
      }));
    }
    window.visualViewport!.addEventListener('resize', onResize);
    return () => window.visualViewport!.removeEventListener('resize', onResize);
  }, [running]);

  useEffect(() => {
    if (!autoRun) return;
    start();
    const timer = setTimeout(() => stop(), 2000);
    return () => clearTimeout(timer);
  }, [autoRun, autoNonce]);

  return (
    <TestCard title={t('mobile.form.title', language)} description={t('mobile.form.desc', language)}>
      <div className="flex flex-wrap gap-2.5 items-center">
        <button className="btn btn-primary" onClick={start} disabled={running || autoRun}>{t('btn.start', language)}</button>
        <button className="btn btn-danger" onClick={stop} disabled={!running || autoRun}>{t('btn.stop', language)}</button>
        <button className="btn" onClick={reset} disabled={autoRun}>{t('btn.reset', language)}</button>
      </div>
      <div className="grid grid-cols-4 gap-2.5 mt-3 max-lg:grid-cols-2 max-sm:grid-cols-1">
        <MetricBox label={t('form.keyboard', language)} value={metrics.keyboardDetected ? 'Detected' : 'Tap input below'} hint="Via Visual Viewport API" />
        <MetricBox label={t('form.inputTypes', language)} value={`${metrics.supportedTypes}/${metrics.totalTypes}`} />
        <MetricBox label={t('form.autofill', language)} value={metrics.autofillSupported ? t('supported.yes', language) : t('supported.no', language)} />
        <MetricBox label="Viewport resizes" value={String(metrics.viewportResizes)} />
      </div>
      <div className="h-px bg-border my-3.5" />
      {running && (
        <div className="grid grid-cols-2 gap-3 max-sm:grid-cols-1">
          {INPUT_TYPES.map((type) => (
            <div key={type}>
              <div className="text-xs text-muted font-mono mb-1">type="{type}"</div>
              <input type={type} className="input w-full" placeholder={type} autoComplete={type === 'email' ? 'email' : undefined} />
            </div>
          ))}
        </div>
      )}
    </TestCard>
  );
}
